import React, { useState } from 'react';
import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { Calendar, Clock, User } from 'lucide-react';
import Footer from '../../components/layout/Footer';
import SEO, { createWebsiteJsonLd } from '../../components/seo/SEO';
import { blogPosts, categories } from '../../data/blogPosts';

export default function BlogListScreen(): React.JSX.Element {
  const [activeCategory, setActiveCategory] = useState('All');
  const filtered = activeCategory === 'All' ? blogPosts : blogPosts.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Blog"
        description="Practical guides, case studies, and industry analysis on AI & Finance for Indian finance professionals."
        canonicalPath="/blog"
        jsonLd={createWebsiteJsonLd()}
      />
      {/* Hero */}
      <section className="pt-32 pb-16 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-500 mb-6"
          >
            Blog
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-4"
          >
            Insights on AI & Finance
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-600 max-w-xl mx-auto"
          >
            Practical guides, case studies, and industry analysis for finance professionals.
          </motion.p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-4 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto flex flex-wrap gap-2 justify-center">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                activeCategory === cat
                  ? 'bg-amber-500 text-white shadow-md shadow-amber-500/20'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((post, i) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="group"
              >
                <Link to={`/blog/${post.slug}`}>
                  <article className="bg-white rounded-2xl overflow-hidden border border-slate-100 hover:shadow-xl transition-all h-full flex flex-col">
                    <div className="h-48 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-600 text-xs font-bold">
                          {post.category}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-amber-500 transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-sm text-slate-600 line-clamp-2 mb-4 flex-1">{post.excerpt}</p>
                      <footer className="flex items-center justify-between text-xs text-slate-500 mt-auto">
                        <address className="flex items-center gap-3 not-italic">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" /> <span rel="author">{post.author}</span>
                          </span>
                          <time dateTime={post.date} className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> {post.date}
                          </time>
                        </address>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {post.readTime}
                        </span>
                      </footer>
                    </div>
                  </article>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
